import React, { useState } from "react";
import { 
  Box, 
  Typography, 
  IconButton, 
  Menu, 
  MenuItem, 
  Avatar,
  Chip,
  Fade,
  Tooltip
} from "@mui/material";
import { getMm } from "../../theme/modernMaterialEnterprise";
import {
  ExpandMore as ExpandMoreIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../contexts/ThemeContext";

const LanguageSwitcher = ({ compact = false, minimal = false }) => {
  const { i18n, t } = useTranslation();
  const { isDarkMode } = useTheme();
  const currentLanguage = i18n.language;
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const languages = [
    { code: "en", name: "English", flag: "/flags/en.png", native: "English" },
    { code: "sv", name: "Svenska", flag: "/flags/sv.png", native: "Svenska" },
    { code: "pt", name: "Português", flag: "/flags/br.png", native: "Português" },
    { code: "es", name: "Español", flag: "/flags/es.png", native: "Español" }
  ];

  const currentLang = languages.find(lang => lang.code === currentLanguage) || languages[0];

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (languageCode) => {
    i18n.changeLanguage(languageCode);
    // Save language preference to localStorage
    try {
      localStorage.setItem("language", languageCode);
    } catch (error) {
      // Handle cases where localStorage might not be available (e.g., private browsing)
      console.warn("Failed to save language preference to localStorage:", error);
    }
    handleClose();
  };

  const languageMenu = (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      TransitionComponent={Fade}
      PaperProps={{
        sx: {
          backgroundColor: isDarkMode ? "rgba(0,0,0,0.95)" : "rgba(255,255,255,0.95)",
          backdropFilter: "blur(20px)",
          border: isDarkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)",
          borderRadius: "16px",
          boxShadow: isDarkMode ? "0 8px 32px rgba(0,0,0,0.3)" : "0 8px 32px rgba(0,0,0,0.1)",
          mt: 1,
          minWidth: 200,
          overflow: "hidden",
        },
      }}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "center",
      }}
    >
      {languages.map((language) => (
        <MenuItem
          key={language.code}
          onClick={() => handleLanguageChange(language.code)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
            gap: 2,
            backgroundColor:
              language.code === currentLanguage
                ? getMm(isDarkMode).hoverWash
                : "transparent",
            borderBottom: isDarkMode ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(0,0,0,0.05)",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: getMm(isDarkMode).hoverSubtle,
              transform: "translateX(4px)",
            },
            "&:last-child": {
              borderBottom: "none",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
            <Avatar
              src={language.flag}
              alt={language.name}
              sx={{
                width: 32,
                height: 32,
                border: "2px solid rgba(255,255,255,0.2)",
                boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: getMm(isDarkMode).primary,
                fontWeight: 600,
                fontSize: "0.9rem",
                lineHeight: 1.2,
              }}
            >
              {language.native}
            </Typography>
          </Box>

          {language.code === currentLanguage && (
            <Chip
              icon={<CheckIcon />}
              label={t("current")}
              size="small"
              sx={{
                backgroundColor: "rgba(76, 175, 80, 0.2)",
                color: "#4caf50",
                border: "1px solid rgba(76, 175, 80, 0.3)",
                fontSize: "0.7rem",
                height: 24,
                "& .MuiChip-icon": {
                  fontSize: "0.8rem",
                },
              }}
            />
          )}
        </MenuItem>
      ))}
    </Menu>
  );

  if (minimal) {
    return (
      <>
        <Tooltip title={t("change_language")} arrow>
          <IconButton
            onClick={handleClick}
            size="small"
            aria-label={t("change_language")}
            sx={{
              width: 36,
              height: 36,
              borderRadius: "8px",
              border: `1px solid ${getMm(isDarkMode).outlineVariant}`,
              backgroundColor: getMm(isDarkMode).surfaceContainer,
              p: 0.5,
              "&:hover": {
                backgroundColor: getMm(isDarkMode).outlineVariant,
              },
            }}
          >
            <Avatar
              src={currentLang.flag}
              alt={currentLang.name}
              sx={{ width: 22, height: 22 }}
            />
          </IconButton>
        </Tooltip>
        {languageMenu}
      </>
    );
  }

  return (
    <Box sx={{ width: '100%', height: compact ? '100%' : 'auto' }}>
      <Tooltip title={t("change_language")} arrow>
        <Box
          onClick={handleClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: compact ? 1 : 1.5,
            minHeight: compact ? 44 : 52,
            height: compact ? '100%' : undefined,
            backgroundColor: getMm(isDarkMode).hoverSubtle,
            borderRadius: '12px',
            border: isDarkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.1)',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
              borderColor: isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)',
              transform: 'translateY(-1px)',
              boxShadow: isDarkMode ? '0 4px 12px rgba(0,0,0,0.15)' : '0 4px 12px rgba(0,0,0,0.1)'
            },
            '&:active': {
              transform: 'translateY(0px) scale(0.98)'
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              src={currentLang.flag}
              alt={currentLang.name}
              sx={{
                width: compact ? 24 : 28,
                height: compact ? 24 : 28,
                border: '2px solid rgba(255,255,255,0.2)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
              }}
            />
                         <Typography
               variant="body2"
               sx={{
                 color: getMm(isDarkMode).primary,
                 fontWeight: 600,
                 fontSize: compact ? '0.8rem' : '0.9rem',
                 lineHeight: 1.2
               }}
             >
               {currentLang.native}
             </Typography>
          </Box>
          
          <ExpandMoreIcon
            sx={{
              color: isDarkMode ? 'rgba(255,255,255,0.7)' : 'rgba(44,62,80,0.7)',
              fontSize: compact ? '1rem' : '1.2rem',
              transition: 'transform 0.3s ease',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)'
            }}
          />
        </Box>
      </Tooltip>

      {languageMenu}
    </Box>
  );
};

export default LanguageSwitcher;
